import { createFileRoute } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { Copy, Link2, Pencil, Plus, Trash2 } from 'lucide-react'
import { QRCodeSVG } from 'qrcode.react'
import { toast } from 'sonner'

import { CareLogMultiSelect } from './-care-log-multi-select'
import type { TeaLot } from '@/lib/api/tea-lots-api'
import type { TeaLotFormValues } from '@/lib/schemas/tea-lot-schemas'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { GrowerSearchSelect } from '@/components/growers/grower-search-select'
import { useAuth } from '@/hooks/use-auth'
import { useProductClassifications } from '@/hooks/use-product-classifications-query'
import { useWeighings } from '@/hooks/use-weighings-query'
import {
    useCreateTeaLot,
    useDeleteTeaLot,
    useTeaLots,
    useUpdateTeaLot,
} from '@/hooks/use-tea-lots-query'
import { traceCodesApi } from '@/lib/api/trace-codes-api'
import { m } from '@/lib/paraglide/messages'
import { createTeaLotSchema } from '@/lib/schemas/tea-lot-schemas'

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/agriculture/tea-lots',
)({
    component: TeaLotsPage,
})


function TeaLotsPage() {
    const { hasPermission } = useAuth()
    const canWrite = hasPermission('tea_lots.write')
    const canDelete = hasPermission('tea_lots.delete')
    const canMintTrace = hasPermission('trace_codes.write')

    const [growerFilter, setGrowerFilter] = useState('')
    const [editing, setEditing] = useState<TeaLot | null>(null)
    const [creating, setCreating] = useState(false)
    const [traceInfo, setTraceInfo] = useState<{
        lotId: string
        code: string
    } | null>(null)

    const { data } = useTeaLots({
        per_page: 100,
        grower_id: growerFilter || undefined,
    })
    const rows = data?.data ?? []

    const createMut = useCreateTeaLot()
    const updateMut = useUpdateTeaLot(editing?.id ?? '')
    const deleteMut = useDeleteTeaLot()

    const schema = createTeaLotSchema()
    const form = useForm<TeaLotFormValues>({
        resolver: zodResolver(schema),
        defaultValues: {
            grower_id: '',
            weighing_id: undefined,
            lot_date: '',
            quantity: 0,
            status: '',
            ballot_returned: false,
            is_active: true,
            classification_ids: [],
        },
    })

    const isOpen = creating || editing !== null

    function openCreate() {
        form.reset({
            grower_id: '',
            weighing_id: undefined,
            lot_date: new Date().toISOString().slice(0, 10),
            quantity: 0,
            status: '',
            ballot_returned: false,
            is_active: true,
            classification_ids: [],
        })
        setCreating(true)
    }

    function openEdit(lot: TeaLot) {
        form.reset({
            grower_id: lot.grower_id,
            weighing_id: lot.weighing_id ?? undefined,
            lot_date: lot.lot_date.slice(0, 10),
            quantity: lot.quantity,
            status: lot.status ?? '',
            ballot_returned: lot.ballot_returned,
            is_active: lot.is_active,
            // Hydrate existing links so editing doesn't silently wipe them.
            // The list endpoint returns classifications only via GetByID; use
            // whatever was hydrated in the row, fall back to empty.
            classification_ids: lot.classifications?.map((c) => c.id) ?? [],
        })
        setEditing(lot)
    }

    function closeDialog() {
        setCreating(false)
        setEditing(null)
        form.reset()
    }

    function onSubmit(values: TeaLotFormValues) {
        const payload = {
            grower_id: values.grower_id,
            weighing_id: values.weighing_id || null,
            lot_date: values.lot_date,
            quantity: values.quantity,
            status: values.status || null,
            ballot_returned: values.ballot_returned,
            is_active: values.is_active,
            classification_ids: values.classification_ids,
        }
        if (editing) {
            updateMut.mutate(payload, { onSuccess: closeDialog })
        } else {
            createMut.mutate(payload, { onSuccess: closeDialog })
        }
    }

    function handleDelete(lot: TeaLot) {
        if (!confirm(m.tea_lot_delete_confirm())) return
        deleteMut.mutate(lot.id)
    }

    async function handleMintTrace(lot: TeaLot) {
        try {
            const res = await traceCodesApi.mint({
                origin_type: 'PRODUCT',
                origin_id: lot.id,
            })
            const code = res.data?.short_code
            if (code) {
                setTraceInfo({ lotId: lot.id, code })
            }
        } catch {
            toast.error(m.toast_trace_mint_error())
        }
    }

    const isSaving = createMut.isPending || updateMut.isPending
    const traceUrl = traceInfo
        ? `${window.location.origin}/trace/${traceInfo.code}`
        : ''

    return (
        <div className="p-6 space-y-4">
            <div className="flex items-center justify-between">
                <h1 className="text-2xl font-semibold">{m.tea_lot_title()}</h1>
                {canWrite && (
                    <Button onClick={openCreate}>
                        <Plus className="mr-2 h-4 w-4" />
                        {m.tea_lot_new()}
                    </Button>
                )}
            </div>

            {/* Grower filter */}
            <div className="flex gap-3">
                <GrowerSearchSelect
                    value={growerFilter || undefined}
                    onChange={setGrowerFilter}
                    placeholder={m.weighing_filter_all_growers()}
                    allowClear
                    className="w-48"
                />
            </div>

            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead>{m.tea_lot_code()}</TableHead>
                        <TableHead>{m.tea_lot_grower()}</TableHead>
                        <TableHead>{m.tea_lot_date()}</TableHead>
                        <TableHead>{m.tea_lot_quantity()}</TableHead>
                        <TableHead>{m.tea_lot_status()}</TableHead>
                        <TableHead>{m.tea_lot_ballot_returned()}</TableHead>
                        <TableHead />
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {rows.map((lot) => (
                        <TableRow key={lot.id}>
                            <TableCell className="font-mono text-sm">
                                {lot.lot_code}
                            </TableCell>
                            <TableCell>
                                {lot.grower_name || '—'}
                            </TableCell>
                            <TableCell>{lot.lot_date.slice(0, 10)}</TableCell>
                            <TableCell>{lot.quantity}</TableCell>
                            <TableCell>
                                {lot.status ? (
                                    <Badge variant="secondary">
                                        {lot.status}
                                    </Badge>
                                ) : (
                                    '—'
                                )}
                            </TableCell>
                            <TableCell>
                                <Badge
                                    variant={
                                        lot.ballot_returned
                                            ? 'default'
                                            : 'secondary'
                                    }
                                >
                                    {lot.ballot_returned
                                        ? m.common_yes()
                                        : m.common_no()}
                                </Badge>
                            </TableCell>
                            <TableCell className="text-right space-x-1">
                                {canMintTrace && (
                                    <Button
                                        variant="outline"
                                        size="icon"
                                        onClick={() => handleMintTrace(lot)}
                                        title={m.tea_lot_trace_action()}
                                    >
                                        <Link2 className="h-4 w-4" />
                                    </Button>
                                )}
                                {canWrite && (
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => openEdit(lot)}
                                    >
                                        <Pencil className="h-4 w-4" />
                                    </Button>
                                )}
                                {canDelete && (
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => handleDelete(lot)}
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </Button>
                                )}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            {/* Create / Edit dialog */}
            <TeaLotFormDialog
                open={isOpen}
                title={editing ? m.tea_lot_edit() : m.tea_lot_new()}
                lotCode={editing?.lot_code ?? null}
                growerName={editing?.grower_name}
                form={form}
                onSubmit={onSubmit}
                isSaving={isSaving}
                onClose={closeDialog}
            />

            {/* Trace code dialog */}
            {traceInfo && (
                <Dialog
                    open
                    onOpenChange={(o) => {
                        if (!o) setTraceInfo(null)
                    }}
                >
                    <DialogContent className="max-w-sm">
                        <DialogHeader>
                            <DialogTitle>
                                {m.trace_code_dialog_title()}
                            </DialogTitle>
                        </DialogHeader>
                        <div className="space-y-3">
                            <div className="space-y-1">
                                <Label className="text-xs text-muted-foreground">
                                    {m.trace_code_label()}
                                </Label>
                                <p className="font-mono text-lg font-semibold">
                                    {traceInfo.code}
                                </p>
                            </div>
                            <div className="space-y-1">
                                <Label className="text-xs text-muted-foreground">
                                    {m.trace_url_label()}
                                </Label>
                                <div className="flex items-center gap-2">
                                    <p className="text-sm text-muted-foreground truncate flex-1">
                                        {traceUrl}
                                    </p>
                                    <Button
                                        variant="outline"
                                        size="icon"
                                        onClick={() => {
                                            void navigator.clipboard.writeText(
                                                traceUrl,
                                            )
                                            toast.success(m.toast_copied())
                                        }}
                                    >
                                        <Copy className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>
                            <div className="flex justify-center pt-1">
                                <QRCodeSVG value={traceUrl} size={160} />
                            </div>
                        </div>
                    </DialogContent>
                </Dialog>
            )}
        </div>
    )
}

function TeaLotFormDialog({
    open,
    title,
    lotCode,
    growerName,
    form,
    onSubmit,
    isSaving,
    onClose,
}: {
    open: boolean
    title: string
    lotCode: string | null
    /** Tên hộ của lô đang sửa — giữ nhãn khi hộ đã bị xoá mềm. */
    growerName?: string
    form: ReturnType<typeof useForm<TeaLotFormValues>>
    onSubmit: (v: TeaLotFormValues) => void
    isSaving: boolean
    onClose: () => void
}) {
    const { data: weighingsData } = useWeighings({ per_page: 200 })
    const weighings = weighingsData?.data ?? []
    const { data: classificationsData } = useProductClassifications({
        per_page: 200,
    })
    const classifications = classificationsData?.data ?? []

    const classificationOptions = useMemo(
        () => classifications.map((c) => ({ value: c.id, label: c.name })),
        [classifications],
    )

    return (
        <Dialog
            open={open}
            onOpenChange={(o) => {
                if (!o) onClose()
            }}
        >
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle>{title}</DialogTitle>
                </DialogHeader>
                <form
                    id="tea-lot-form"
                    onSubmit={form.handleSubmit(onSubmit)}
                    className="space-y-4"
                >
                    {/* Lot code (read-only — auto-generated on create) */}
                    <div className="space-y-1">
                        <Label>{m.tea_lot_code()}</Label>
                        <Input
                            value={lotCode ?? ''}
                            placeholder={m.tea_lot_code_auto_hint()}
                            readOnly
                            disabled
                            className="font-mono"
                        />
                    </div>

                    {/* Grower */}
                    <div className="space-y-1">
                        <Label>{m.tea_lot_grower()}</Label>
                        <GrowerSearchSelect
                            value={form.watch('grower_id')}
                            onChange={(v) =>
                                form.setValue('grower_id', v, {
                                    shouldValidate: true,
                                })
                            }
                            placeholder={m.weighing_select_grower()}
                            fallbackLabel={growerName}
                        />
                        {form.formState.errors.grower_id && (
                            <p className="text-destructive text-sm">
                                {form.formState.errors.grower_id.message}
                            </p>
                        )}
                    </div>

                    {/* Weighing (optional) */}
                    <div className="space-y-1">
                        <Label>{m.tea_lot_weighing()}</Label>
                        <Select
                            value={form.watch('weighing_id') ?? '__none__'}
                            onValueChange={(v) =>
                                form.setValue(
                                    'weighing_id',
                                    v === '__none__' ? undefined : v,
                                )
                            }
                        >
                            <SelectTrigger>
                                <SelectValue
                                    placeholder={m.tea_lot_weighing_none()}
                                />
                            </SelectTrigger>
                            <SelectContent>
                                {/* Radix Select crashes on value=""; use sentinel */}
                                <SelectItem value="__none__">
                                    {m.tea_lot_weighing_none()}
                                </SelectItem>
                                {weighings.map((w) => (
                                    <SelectItem key={w.id} value={w.id}>
                                        {w.weigh_date.slice(0, 10)} —{' '}
                                        {w.quantity} kg
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Lot date */}
                    <div className="space-y-1">
                        <Label>{m.tea_lot_date()}</Label>
                        <Input type="date" {...form.register('lot_date')} />
                        {form.formState.errors.lot_date && (
                            <p className="text-destructive text-sm">
                                {form.formState.errors.lot_date.message}
                            </p>
                        )}
                    </div>

                    {/* Quantity */}
                    <div className="space-y-1">
                        <Label>{m.tea_lot_quantity()}</Label>
                        <Input
                            type="number"
                            step="0.01"
                            min="0"
                            {...form.register('quantity')}
                        />
                    </div>

                    {/* Status */}
                    <div className="space-y-1">
                        <Label>{m.tea_lot_status()}</Label>
                        <Input {...form.register('status')} />
                    </div>

                    {/* Classifications (M-N) */}
                    <div className="space-y-1">
                        <Label>{m.tea_lot_classifications()}</Label>
                        <CareLogMultiSelect
                            options={classificationOptions}
                            selected={form.watch('classification_ids')}
                            onChange={(v) =>
                                form.setValue('classification_ids', v)
                            }
                            placeholder={m.tea_lot_classifications_placeholder()}
                        />
                    </div>

                    {/* Ballot returned */}
                    <div className="flex items-center gap-2">
                        <Switch
                            checked={form.watch('ballot_returned')}
                            onCheckedChange={(v) =>
                                form.setValue('ballot_returned', v)
                            }
                        />
                        <Label>{m.tea_lot_ballot_returned()}</Label>
                    </div>

                    {/* Is active */}
                    <div className="flex items-center gap-2">
                        <Switch
                            checked={form.watch('is_active')}
                            onCheckedChange={(v) =>
                                form.setValue('is_active', v)
                            }
                        />
                        <Label>{m.common_is_active()}</Label>
                    </div>
                </form>
                <DialogFooter>
                    <Button
                        form="tea-lot-form"
                        type="submit"
                        disabled={isSaving}
                    >
                        {m.tea_lot_save()}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
